import { FC, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from "@mui/material";
import { Cancel, Save } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import {
  useCreateSessionHook,
  useUpdateSessionHook,
} from "../../../hooks/useLessonPlanHook";
import { logger } from "../../../utils/common";
import {
  TCreateSessionRequest,
  TLessonPlanSession,
} from "../../../apis/lessonPlan/lessonPlan.interfaces";

const sessionSchema = z.object({
  sessionNumber: z.coerce
    .number({ invalid_type_error: "Session number is required" })
    .int("Session number must be a whole number")
    .min(1, "Session number must be at least 1"),
  title: z
    .string()
    .min(1, "Title is required")
    .max(200, "Title must not exceed 200 characters"),
  description: z.string().max(2000).optional().or(z.literal("")),
  duration: z
    .union([
      z.coerce.number().int("Duration must be a whole number").min(1),
      z.literal(""),
    ])
    .optional(),
  materialsRequiredText: z
    .string()
    .optional()
    .refine(
      (raw) => {
        if (!raw) return true;
        return (
          raw
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean).length <= 50
        );
      },
      "At most 50 materials allowed",
    ),
  homework: z
    .string()
    .max(5000, "Homework must not exceed 5000 characters")
    .optional()
    .or(z.literal("")),
  notes: z
    .string()
    .max(5000, "Notes must not exceed 5000 characters")
    .optional()
    .or(z.literal("")),
});

type SessionFormData = z.infer<typeof sessionSchema>;

interface ManageSessionDialogProps {
  open: boolean;
  onClose: () => void;
  lessonPlanId: string;
  unitId: string;
  session?: TLessonPlanSession | null;
}

const buildDefaults = (session?: TLessonPlanSession | null): SessionFormData => ({
  sessionNumber: session?.sessionNumber ?? 1,
  title: session?.title ?? "",
  description: session?.description ?? "",
  duration: session?.duration ?? "",
  materialsRequiredText: session?.materialsRequired?.join("\n") ?? "",
  homework: session?.homework ?? "",
  notes: session?.notes ?? "",
});

const ManageSessionDialog: FC<ManageSessionDialogProps> = ({
  open,
  onClose,
  lessonPlanId,
  unitId,
  session,
}) => {
  const isEditMode = !!session;
  const createMutation = useCreateSessionHook();
  const updateMutation = useUpdateSessionHook();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SessionFormData>({
    resolver: zodResolver(sessionSchema),
    defaultValues: buildDefaults(session),
  });

  useEffect(() => {
    if (open) reset(buildDefaults(session));
  }, [open, session, reset]);

  const onSubmit = async (data: SessionFormData) => {
    const materialsRequired = (data.materialsRequiredText ?? "")
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    const payload: TCreateSessionRequest = {
      sessionNumber: data.sessionNumber,
      title: data.title,
      description: data.description || undefined,
      duration:
        typeof data.duration === "number" ? data.duration : undefined,
      materialsRequired: materialsRequired.length ? materialsRequired : undefined,
      homework: data.homework || undefined,
      notes: data.notes || undefined,
    };

    try {
      if (isEditMode && session) {
        await updateMutation.mutateAsync({
          lessonPlanId,
          unitId,
          sessionId: session.id,
          request: payload,
        });
        logger.show_success("Session updated.");
      } else {
        await createMutation.mutateAsync({
          lessonPlanId,
          unitId,
          request: payload,
        });
        logger.show_success("Session created.");
      }
      onClose();
    } catch (err) {
      logger.show_error(
        err instanceof Error
          ? err.message
          : `Failed to ${isEditMode ? "update" : "create"} session.`,
      );
    }
  };

  const activeMutation = isEditMode ? updateMutation : createMutation;
  const isLoading = isSubmitting || activeMutation.isPending;
  const isError = activeMutation.isError;
  const errorMessage = activeMutation.error?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="manage-session-dialog"
    >
      <DialogTitle>{isEditMode ? "Edit Session" : "Add Session"}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage || "Failed to save session."}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <ControlledTextField
              control={control}
              name="sessionNumber"
              id="session-number"
              label="Session Number"
              required
              type="number"
              errorMessage={errors.sessionNumber?.message}
              inputProps={{ min: 1 }}
            />
            <ControlledTextField
              control={control}
              name="title"
              id="session-title"
              label="Title"
              required
              errorMessage={errors.title?.message}
            />
            <ControlledTextField
              control={control}
              name="description"
              id="session-description"
              label="Description"
              multiline
              rows={2}
              errorMessage={errors.description?.message}
            />
            <ControlledTextField
              control={control}
              name="duration"
              id="session-duration"
              label="Duration (minutes)"
              type="number"
              errorMessage={errors.duration?.message}
              inputProps={{ min: 1 }}
            />
            <ControlledTextField
              control={control}
              name="materialsRequiredText"
              id="session-materials"
              label="Materials Required (one per line)"
              multiline
              rows={3}
              errorMessage={errors.materialsRequiredText?.message}
              helperText="Optional. Up to 50 entries."
            />
            <ControlledTextField
              control={control}
              name="homework"
              id="session-homework"
              label="Homework"
              multiline
              rows={2}
              errorMessage={errors.homework?.message}
            />
            <ControlledTextField
              control={control}
              name="notes"
              id="session-notes"
              label="Notes"
              multiline
              rows={2}
              errorMessage={errors.notes?.message}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading} startIcon={<Cancel />}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
          >
            {isLoading
              ? isEditMode
                ? "Saving..."
                : "Creating..."
              : isEditMode
                ? "Save Changes"
                : "Create Session"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageSessionDialog;
